
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Helmet } from "react-helmet-async";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useUserProfile } from "@/hooks/use-user";
import RequestHeader from "@/components/request/RequestHeader";
import RequestDetailsCard from "@/components/request/RequestDetailsCard";
import RequestBudgetCard from "@/components/request/RequestBudgetCard";
import RequestReviewSection from "@/components/request/RequestReviewSection";
import { fetchBuyerRequestById } from "@/lib/api/requests";
import OpenGraphImage from "@/components/common/OpenGraphImage";
import { generateOgDescription, getOgImagePath } from "@/lib/utils/ogImageUtils";

// This interface should match the structure from fetchBuyerRequestById
interface DetailedRequest {
  id: string;
  niche: string;
  drMin: number;
  daMin: number;
  budgetMin: number;
  budgetMax: number;
  linkType: string;
  notes: string;
  expiresAt: string;
  createdAt: string;
  userId: string;
  userName?: string | null;
  userAvatar?: string | null;
}

// Mock data kept only as fallback for development or testing
const mockRequest: DetailedRequest = {
  id: "request-1234",
  niche: "SaaS & Technology",
  drMin: 40,
  daMin: 35,
  budgetMin: 100,
  budgetMax: 300,
  linkType: "Guest Post",
  notes: "Looking for high-quality technology blogs with real traffic. The content should be well-written and focused on SaaS solutions for small businesses. Prefer sites with engaged audiences and good social media presence.",
  expiresAt: "2025-06-01",
  createdAt: "2025-05-01",
  userId: "buyer-789",
  userName: "TechStartup Inc.",
  userAvatar: null
};

const RequestDetails = () => {
  const { requestId } = useParams<{ requestId: string }>();
  const { profile } = useUserProfile();
  
  const { data: request, isLoading, isError } = useQuery({
    queryKey: ['buyerRequest', requestId],
    queryFn: async () => {
      if (!requestId) {
        console.error("No request ID provided");
        throw new Error("No request ID provided");
      }
      
      // Use the actual API function to fetch the request
      console.log("Fetching buyer request with ID:", requestId);
      const result = await fetchBuyerRequestById(requestId);
      
      if (!result) {
        console.error("Request not found:", requestId);
        throw new Error("Request not found");
      }
      
      console.log("Fetched request:", result);
      return result;
    },
    retry: 1,
    refetchOnWindowFocus: false,
  });

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="h-8 bg-gray-200 rounded w-3/4 animate-pulse"></div>
        <div className="h-32 bg-gray-200 rounded animate-pulse"></div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="h-64 bg-gray-200 rounded animate-pulse"></div>
          <div className="md:col-span-2 h-64 bg-gray-200 rounded animate-pulse"></div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Failed to Load Request</h2>
        <p className="text-gray-600 mb-6">
          There was a problem loading the request details. Please try again.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Button onClick={() => window.location.reload()}>
            Reload Page
          </Button>
          <Link to="/buyer-requests">
            <Button variant="outline">
              Back to Buyer Requests
            </Button>
          </Link>
        </div>
      </div>
    );
  }

  if (!request) {
    return (
      <div className="text-center py-12">
        <AlertCircle className="h-12 w-12 text-amber-500 mx-auto mb-4" />
        <h2 className="text-2xl font-bold mb-2">Request Not Found</h2>
        <p className="text-gray-600 mb-6">
          The request you're looking for doesn't exist or has been removed.
        </p>
        <Link to="/buyer-requests">
          <Button>Browse All Requests</Button>
        </Link>
      </div>
    );
  }

  return (
    <>
      <OpenGraphImage 
        title={`${request.linkType || 'Link'} Request in ${request.niche} | BackLink Hub`}
        description={generateOgDescription('buyer-request', request)}
        image={getOgImagePath('buyer-request', request)}
        type="article"
      />
      
      <div className="bg-gray-50 py-12">
        <div className="container mx-auto px-4">
          <RequestHeader
            title={`${request?.linkType || 'Link'} Request in ${request?.niche}`}
            buyerName={request?.userName || "Anonymous"}
            buyerAvatar={request?.userAvatar}
            createdAt={request?.createdAt}
          />

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2 space-y-8">
              <RequestDetailsCard
                notes={request?.notes || ""}
                niche={request?.niche || ""}
                linkType={request?.linkType || ""}
                drMin={request?.drMin || 0}
                daMin={request?.daMin || 0}
              />
            </div>
            
            <div>
              <RequestBudgetCard
                budgetMin={request?.budgetMin || 0}
                budgetMax={request?.budgetMax || 0}
                expiresAt={request?.expiresAt || ""}
              />
            </div>
          </div>
          
          {/* Only render the review section if we have a valid request */}
          {request && (
            <RequestReviewSection
              buyerId={request.userId} // Use the actual userId from the fetched request
              buyerName={request.userName || "Anonymous"}
              requestId={requestId || ""}
              currentUserId={profile?.id || null}
            />
          )}
        </div>
      </div>
    </>
  );
};

export default RequestDetails;
